import { TransitionDirection, WorkflowPhase, WorkspaceCode } from '@atlas/types'
import { ListBoxItem, Select } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { Eyebrow, PageHeader } from '@/components/editorial/PageHeader'
import type { WorkflowStatusDTO } from '@/domain/workflow-admin.types'
import type { BuilderEdge, BuilderNode } from '@/domain/workflow-builder-layout'
import { buildEdges, layoutNodes, pairKey } from '@/domain/workflow-builder-layout'
import { useChecklistTemplates } from '@/hooks/useChecklistTemplateAdmin'
import {
	useCreateWorkflowStatus,
	useCreateWorkflowTransition,
	useDeleteWorkflowStatus,
	useDeleteWorkflowTransition,
	useReorderWorkflowStatuses,
	useUpdateWorkflowStatus,
	useUpdateWorkflowTransition,
	useWorkflowStatuses,
	useWorkflows,
	useWorkflowTransitions,
} from '@/hooks/useWorkflowAdmin'
import { notifyError } from './notifyError'
import { type BuilderSelection, WorkflowBuilderCanvas } from './WorkflowBuilderCanvas'
import { BuilderInspector } from './WorkflowBuilderInspector'

/** Selection + click-to-connect state — pure UI state, no network calls. */
function useBuilderSelection() {
	const [selection, setSelection] = useState<BuilderSelection>(null)
	const [connecting, setConnecting] = useState(false)
	const [connectFromId, setConnectFromId] = useState<string | null>(null)

	const toggleConnect = () => {
		setConnecting((c) => !c)
		setConnectFromId(null)
		setSelection(null)
	}

	return { selection, setSelection, connecting, setConnecting, connectFromId, setConnectFromId, toggleConnect }
}

/**
 * Resolves a connect-mode second click against the existing edge list: reuse
 * a forward match, add a backward leg to its reverse, or create a fresh
 * connector. A fresh connector's `dir` is NOT always 'forward': it's backward
 * whenever the source status sits later in the pipeline than the target
 * (by `position`) — the same rule the transition engine itself uses to
 * classify and gate backward moves, so the builder's own label can't disagree
 * with what the running system will actually enforce.
 */
function resolveConnectClick(
	connectFromId: string,
	targetId: string,
	edges: readonly BuilderEdge[],
	nodesById: Map<string, BuilderNode>,
):
	| { kind: 'select'; edgeKey: string; dir: 'forward' | 'backward' }
	| { kind: 'create'; edgeKey: string; dir: 'forward' | 'backward' } {
	const forwardMatch = edges.find((e) => e.fromId === connectFromId && e.toId === targetId)
	if (forwardMatch) return { kind: 'select', edgeKey: forwardMatch.key, dir: 'forward' }
	const reverseMatch = edges.find((e) => e.fromId === targetId && e.toId === connectFromId)
	if (reverseMatch) return { kind: 'create', edgeKey: reverseMatch.key, dir: 'backward' }
	const fromPosition = nodesById.get(connectFromId)?.position ?? 0
	const toPosition = nodesById.get(targetId)?.position ?? 0
	const dir = fromPosition > toPosition ? 'backward' : 'forward'
	return { kind: 'create', edgeKey: pairKey(connectFromId, targetId), dir }
}

/**
 * The sequence a move submits: active statuses only, in position order. An
 * inactive status is still drawn on the canvas but holds no place in the
 * order, so it is neither movable nor part of what a reorder renumbers.
 */
const activeStatusOrder = (statuses: readonly WorkflowStatusDTO[]): string[] =>
	statuses
		.filter((status) => status.isActive)
		.slice()
		.sort((a, b) => a.position - b.position)
		.map((status) => status.statusId)

/**
 * `position` is server-owned - a new status is appended after the workflow's
 * last active one whatever the body carries - so the value sent here is the
 * schema's required field and not a placement. Moving the status is the
 * inspector's reorder control.
 */
const newStatusDraft = (workflowId: string, position: number) => ({
	workflowId,
	code: `status_${Math.random().toString(36).slice(2, 8)}`,
	label: 'Untitled status',
	phase: WorkflowPhase.INTAKE,
	position,
	workspaceDefault: WorkspaceCode.RESOLUTION,
	isInitial: false,
	isTerminal: false,
})

/** The second click of a connect-mode gesture: reuse a forward match, add a backward leg, or create a fresh connector — then applies whichever selection/mutation that implies. */
function handleConnectTarget(
	targetId: string,
	sel: ReturnType<typeof useBuilderSelection>,
	edges: readonly BuilderEdge[],
	activeWorkflowId: string | null,
	createTransition: ReturnType<typeof useCreateWorkflowTransition>,
	nodesById: Map<string, BuilderNode>,
) {
	if (!sel.connectFromId) return
	if (sel.connectFromId === targetId) {
		sel.setConnectFromId(null)
		return
	}
	const resolved = resolveConnectClick(sel.connectFromId, targetId, edges, nodesById)
	if (resolved.kind === 'create' && activeWorkflowId) {
		// The row's from/to always match the literal click order (source→target) —
		// `resolved.dir` only tags which role this row plays relative to the
		// connector's existing opposite-direction row, it never swaps who's who.
		createTransition.mutate({
			workflowId: activeWorkflowId,
			fromStatusId: sel.connectFromId,
			toStatusId: targetId,
			ctaLabel: 'New transition',
			direction: resolved.dir === 'forward' ? TransitionDirection.FORWARD : TransitionDirection.BACKWARD,
		})
	}
	sel.setSelection({ type: 'edge', edgeKey: resolved.edgeKey, dir: resolved.dir })
	sel.setConnecting(false)
	sel.setConnectFromId(null)
}

function WorkflowSelector({
	workflows,
	value,
	onChange,
}: {
	workflows: readonly { workflowId: string; name: string; isDefault: boolean }[]
	value: string | undefined
	onChange: (id: string) => void
}) {
	return (
		<div className="max-w-xs">
			<Eyebrow className="mb-1">Workflow</Eyebrow>
			<Select aria-label="Workflow" selectedKey={value} onSelectionChange={(key) => onChange(key as string)}>
				{[...workflows]
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((w) => (
						<ListBoxItem key={w.workflowId} id={w.workflowId}>
							{w.name}
							{w.isDefault ? ' (default)' : ''}
						</ListBoxItem>
					))}
			</Select>
		</div>
	)
}

/**
 * The workflow builder — a visual canvas over the same admin CRUD endpoints
 * the WorkflowAdmin list/detail pages use (`workflow_status`,
 * `workflow_transition`, `transition_hook`). Node positions come from
 * `layoutNodes`, which reads each status's persisted `canvasX`/`canvasY` (or
 * falls back to a computed grid slot for a status never dragged) — dragging
 * calls the real `updateWorkflowStatus` mutation, so it survives a reload.
 */
export function WorkflowBuilderPage() {
	const { data: workflows = [] } = useWorkflows()
	const [workflowId, setWorkflowId] = useState<string | undefined>(undefined)
	const activeWorkflowId =
		workflowId ?? workflows.find((w) => w.isDefault)?.workflowId ?? workflows[0]?.workflowId ?? null

	const { data: statuses = [] } = useWorkflowStatuses(activeWorkflowId)
	const { data: transitions = [] } = useWorkflowTransitions(activeWorkflowId)
	const { data: templates = [] } = useChecklistTemplates()

	const nodes = useMemo(() => layoutNodes(statuses), [statuses])
	const edges = useMemo(() => buildEdges(transitions), [transitions])
	const nodesById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes])
	const orderedStatusIds = useMemo(() => activeStatusOrder(statuses), [statuses])
	const edgesByKey = useMemo(() => new Map(edges.map((e) => [e.key, e])), [edges])

	const sel = useBuilderSelection()
	const createStatus = useCreateWorkflowStatus()
	const updateStatus = useUpdateWorkflowStatus()
	const deleteStatus = useDeleteWorkflowStatus()
	const reorderStatuses = useReorderWorkflowStatuses()
	const createTransition = useCreateWorkflowTransition()
	const updateTransition = useUpdateWorkflowTransition()
	const deleteTransition = useDeleteWorkflowTransition()

	const handleNewStatus = () => {
		if (!activeWorkflowId) return
		createStatus.mutate(newStatusDraft(activeWorkflowId, statuses.length), {
			onSuccess: (created) => sel.setSelection({ type: 'node', id: created.statusId }),
		})
	}

	const handleNodeClick = (id: string) => {
		if (!sel.connecting) {
			sel.setSelection({ type: 'node', id })
			return
		}
		if (!sel.connectFromId) sel.setConnectFromId(id)
		else handleConnectTarget(id, sel, edges, activeWorkflowId, createTransition, nodesById)
	}

	const handleNodeMove = (id: string, x: number, y: number, onError?: () => void) =>
		updateStatus.mutate(
			{ statusId: id, body: { canvasX: x, canvasY: y }, workflowId: activeWorkflowId as string },
			{
				onError: (err) => {
					onError?.()
					notifyError('Could not save the new position')(err)
				},
			},
		)

	const selectedNode: BuilderNode | null =
		sel.selection?.type === 'node' ? (nodesById.get(sel.selection.id) ?? null) : null
	const selectedEdge: BuilderEdge | null =
		sel.selection?.type === 'edge' ? (edgesByKey.get(sel.selection.edgeKey) ?? null) : null

	return (
		<div className="space-y-6">
			<PageHeader
				eyebrow="Admin · Workflow & checklists"
				title="Workflow builder"
				subtitle="Work order statuses, transitions, and the checklist attached to each transition."
			/>

			<WorkflowSelector workflows={workflows} value={activeWorkflowId ?? undefined} onChange={setWorkflowId} />

			<div className="flex gap-3.5">
				<BuilderInspector
					selectedNode={selectedNode}
					selectedEdge={selectedEdge}
					dir={sel.selection?.type === 'edge' ? sel.selection.dir : 'forward'}
					nodesById={nodesById}
					statusCount={statuses.length}
					transitionCount={transitions.length}
					activeWorkflowId={activeWorkflowId}
					templates={templates}
					updateStatus={updateStatus}
					deleteStatus={deleteStatus}
					reorderStatuses={reorderStatuses}
					orderedStatusIds={orderedStatusIds}
					updateTransition={updateTransition}
					createTransition={createTransition}
					deleteTransition={deleteTransition}
					onSwitchDir={(dir) => selectedEdge && sel.setSelection({ type: 'edge', edgeKey: selectedEdge.key, dir })}
					onClearSelection={() => sel.setSelection(null)}
				/>

				<WorkflowBuilderCanvas
					nodes={nodes}
					edges={edges}
					selection={sel.selection}
					connecting={sel.connecting}
					connectFromId={sel.connectFromId}
					onNodeClick={handleNodeClick}
					onNodeMove={handleNodeMove}
					onEdgeLabelClick={(edgeKey, dir) => sel.setSelection({ type: 'edge', edgeKey, dir })}
					onToggleConnect={sel.toggleConnect}
					onNewStatus={handleNewStatus}
				/>
			</div>
		</div>
	)
}
